/**
 * Card de produto reutilizável (Grid View)
 * Segue o mesmo padrão do PropertyCard.tsx
 */

import React from 'react';
import type { DatabaseProductData, DatabaseDocumentResponse, BusinessOpportunity, ProductVariation } from '@troia-v3/types';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons'
type ProductDocument = DatabaseDocumentResponse<DatabaseProductData>;

export interface ProductCardProps {
  product: ProductDocument;
  onClick?: () => void;
  alwaysLight?: boolean;
  /** Menu ••• de ações do card (F39: paridade com Documentos). Omitido = sem kebab. */
  actions?: CardAction[];
}

const categoryLabels: Record<string, string> = {
  electronics: 'Eletrônicos',
  furniture: 'Móveis',
  clothing: 'Vestuário',
  food: 'Alimentos',
  books: 'Livros',
  toys: 'Brinquedos',
  sports: 'Esportes',
  beauty: 'Beleza',
  automotive: 'Automotivo',
  home_garden: 'Casa e Jardim',
  other: 'Outros'
};

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  outofstock: 'Sem estoque',
  discontinued: 'Descontinuado',
  archived: 'Arquivado'
};

const statusColors: Record<string, string> = {
  active: '#10B981',
  inactive: '#6B7280',
  outofstock: '#EF4444',
  discontinued: '#F59E0B',
  archived: '#6B7280'
};

export const formatPrice = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  }
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}mil`;
  }
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const getTotalStock = (data: DatabaseProductData): number | null => {
  if (data.variations && data.variations.length > 0) {
    return data.variations.reduce((sum: number, v: ProductVariation) => sum + (v.stock || 0), 0);
  }
  return data.inventory?.stock ?? null;
};

const getMainPrice = (data: DatabaseProductData): BusinessOpportunity | undefined => {
  const saleOpp = data.opportunities?.find((opp: BusinessOpportunity) => opp.type === 'sale');
  return saleOpp || data.opportunities?.[0];
};

export const ProductCard: React.FC<ProductCardProps> = ({ product, onClick, alwaysLight, actions }) => {
  const productData = product.data;
  const firstPhoto = productData?.media?.photos?.[0];
  const totalStock = productData ? getTotalStock(productData) : null;
  const mainOpportunity = productData ? getMainPrice(productData) : undefined;
  const variationsCount = productData?.variations?.length || 0;

  const cardBg = alwaysLight ? '#ffffff' : 'var(--theme-card-background)';
  const cardBorder = alwaysLight ? '#e5e7eb' : 'var(--theme-card-border)';
  const cardText = alwaysLight ? '#1e293b' : 'var(--theme-card-text)';
  const mutedText = alwaysLight ? '#64748b' : 'var(--theme-muted-text)';
  const mutedBg = alwaysLight ? '#f1f5f9' : 'var(--theme-muted-background, #f1f5f9)';

  return (
    <div
      className="rounded-lg border overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
      style={{ backgroundColor: cardBg, borderColor: cardBorder }}
      onClick={onClick}
      data-testid={`product-card-${product.id}`}
    >
      {/* Imagem com Badge de Preço */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {firstPhoto ? (
          <img
            src={firstPhoto}
            alt={productData?.name || 'Produto'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: mutedBg }}
          >
            <Icon name="package" className="w-10 h-10" style={{ color: mutedText }} />
          </div>
        )}

        {/* Badge de Preço */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {mainOpportunity?.pricing?.amount && (
            <div
              className="px-2 py-1 rounded text-xs font-semibold shadow-sm"
              style={{ backgroundColor: alwaysLight ? '#10B981' : 'var(--theme-success-color)', color: '#ffffff' }}
            >
              {formatPrice(mainOpportunity.pricing.amount)}
            </div>
          )}
        </div>

        {/* Badge de Status */}
        {productData?.itemStatus && (
          <div
            className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium shadow-sm"
            style={{
              backgroundColor: statusColors[productData.itemStatus] || '#6B7280',
              color: '#ffffff'
            }}
          >
            {statusLabels[productData.itemStatus] || productData.itemStatus}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <div className="flex items-start gap-1.5 mb-1.5">
          <h3
            className="flex-1 min-w-0 font-semibold text-sm line-clamp-1"
            style={{ color: cardText }}
          >
            {productData?.name || 'Sem nome'}
          </h3>
          {actions && actions.length > 0 && (
            <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
              <CardActionsMenu actions={actions} size="sm" data-testid={`product-card-actions-${product.id}`} />
            </div>
          )}
        </div>

        {productData?.category && (
          <div className="flex items-center gap-1.5 mb-2.5">
            <Icon name="tag" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: mutedText }} />
            <p className="text-xs line-clamp-1" style={{ color: mutedText }}>
              {categoryLabels[productData.category] || productData.category}
              {productData.brand ? ` · ${productData.brand}` : ''}
            </p>
          </div>
        )}

        <div
          className="flex items-center justify-between pt-2.5 border-t"
          style={{ borderColor: cardBorder }}
        >
          {/* Estoque */}
          <div className="flex items-center gap-1.5">
            {totalStock !== null ? (
              <span
                className="text-xs font-medium"
                style={{
                  color: totalStock === 0
                    ? (alwaysLight ? '#EF4444' : 'var(--theme-error-color)')
                    : cardText
                }}
              >
                {totalStock === 0 ? 'Sem estoque' : `${totalStock} em estoque`}
              </span>
            ) : (
              <span className="text-xs" style={{ color: mutedText }}>
                Estoque não rastreado
              </span>
            )}
          </div>

          {/* Variações */}
          {variationsCount > 0 && (
            <span className="text-xs" style={{ color: mutedText }}>
              {variationsCount} {variationsCount === 1 ? 'variação' : 'variações'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
